<template>
  <section :id="listContent.section_id ? listContent.section_id : false">
    <div :class="$style.heading">
      <Title :title-params="listContent.title" :class="$style.title" />
      <RichText
        :text-params="listContent.description"
        :class="$style.description"
      />
      <YearsList
        v-model="activeYear"
        :years-list="getAvailableYears"
        :class="$style.yearList"
      />
    </div>
    <transition-group name="fade" tag="div" :class="$style.articlesList">
      <div v-if="currentArticleList" :key="activeYear">
        <transition-group name="fade" tag="div" :class="$style.articlesList">
          <template
            v-for="item in currentArticleList.slice(0, articleListLimit)"
          >
            <ReleaseCard
              v-if="listContent.is_release"
              :key="item.id"
              :article="item"
              :class="$style.card"
            />
            <ArticleCard
              v-else
              :key="item.id"
              :article="item"
              :class="$style.card"
            />
          </template>
        </transition-group>
        <LkButton
          v-if="remainingArticles > 0"
          :class="$style.button"
          is-light
          @click="showAllArticles"
        >
          {{ buttonText }}
        </LkButton>
      </div>
    </transition-group>
  </section>
</template>

<script>
import Title from '~/components/Title'
import RichText from '~/components/RichText'

import YearsList from '../YearsList'
import ReleaseCard from '../ReleaseCard'
import ArticleCard from '../ArticleCard'

export default {
  name: 'PressList',

  components: {
    YearsList,
    ReleaseCard,
    ArticleCard,
    Title,
    RichText,
  },

  props: {
    listContent: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      activeYear: '',
      articleListLimit: 4,
    }
  },

  computed: {
    sortedItems() {
      const list = this.listContent.items

      return list
        .filter((item) => item.data.date)
        .reduce((objectsByKeyValue, obj) => {
          const year = new Date(obj.data.date).getFullYear()
          objectsByKeyValue[year] = (objectsByKeyValue[year] || []).concat(obj)
          return objectsByKeyValue
        }, {})
    },

    getAvailableYears() {
      return Object.keys(this.sortedItems).sort((a, b) => b - a)
    },

    buttonText() {
      const subject = this.listContent.is_release
        ? this.$tc('pressRelease', this.remainingArticles)
        : this.$tc('article', this.remainingArticles)

      return this.$tc('seeLastArticle', this.remainingArticles, {
        subject,
        remainingArticles: this.remainingArticles,
      })
    },

    currentArticleList() {
      return this.sortedItems[this.activeYear]
    },

    remainingArticles() {
      return this.currentArticleList.length - this.articleListLimit
    },
  },

  watch: {
    activeYear() {
      this.articleListLimit = 4
    },
  },

  created() {
    if (!this.activeYear) this.activeYear = this.getAvailableYears[0]
  },

  mounted() {
    document.addEventListener('scroll', this.debounce(this.storeScroll), {
      passive: true,
    })

    this.storeScroll()
  },

  methods: {
    showAllArticles() {
      this.articleListLimit = this.currentArticleList.length
    },

    debounce(fn) {
      let frame
      return (...params) => {
        if (frame) {
          cancelAnimationFrame(frame)
        }

        frame = requestAnimationFrame(() => {
          fn(...params)
        })
      }
    },

    storeScroll() {
      const element = document.getElementById(this.listContent.section_id)
      if (element) {
        const offset = window.scrollY - element.offsetTop + 200

        if (offset > 0) this.$emit('title-active', this.listContent.section_id)
        else this.$emit('title-inactive', this.listContent.section_id)
      }
    },
  },
}
</script>

<style lang="scss" module>
.heading {
  padding: 0 25px;
}

.title {
  @include typo-mega-title;
  font-weight: 200;

  @include below(small) {
    @include typo-extra-title;
    font-weight: 200;
  }
}
.description {
  color: $gray-700;
}

.yearList {
  margin: 32px 0 0;
}

.card {
  margin-bottom: 60px;
  &:last-of-type {
    margin-bottom: 0;
  }

  @include below(small) {
    margin-bottom: 50px;
    &:last-of-type {
      margin-bottom: 0;
    }
  }
}

.articlesList {
  position: relative;

  margin-top: 60px;
}
.button {
  margin-top: 60px;
  margin-left: 25px;

  @include below(medium) {
    width: 100%;
    margin-left: 0;
  }
}
</style>
<style lang="scss" scoped>
.fade-enter {
  opacity: 0;
}
.fade-enter-to {
  opacity: 1;
}
.fade-enter-active {
  transition: all 0.5s ease-in-out 0.5s;
}
.fade-leave-active {
  position: relative;

  transition: all 0.6s ease-in-out;
}
.fade-leave {
  position: relative;

  opacity: 0;
}
.fade-leave-to {
  position: relative;

  transform: translateY(35px);

  opacity: 0;
}
</style>
